import { format, isValid } from "date-fns";
import React, { useContext } from "react";
import styled from "styled-components";
import { Box, CircularProgress, Divider, Grid, Stack } from "@mui/material";
import executiveNameLibrary from "src/scenes/overview/overview-components/executive-name-library";
import timeSplitter from "src/utils/time-splitter";
import MaintainabilityTable from "src/scenes/overview/overview-components/details-tables/maintainability-table";
import CodeSilosTable from "src/scenes/overview/overview-components/details-tables/code-silos-table";
import ScopeCreepTable from "src/scenes/overview/overview-components/details-tables/scope-creep-table";
import ArrayTable from "src/scenes/overview/overview-components/details-tables/array-table";
import ArrayTableCredit from "src/scenes/overview/overview-components/details-tables/array-table-credit";
import ReviewTimeTable from "src/scenes/overview/overview-components/details-tables/review-time-table";
import ThroughputTable from "src/scenes/overview/overview-components/details-tables/throughput-table";
import LeadTimeTable from "src/scenes/overview/overview-components/details-tables/lead-time-table";
import LeadTimePmTable from "src/scenes/overview/overview-components/details-tables/lead-pm-table";
import ThroughputPmTable from "src/scenes/overview/overview-components/details-tables/throughput-pm-table";
import request from "src/utils/request";
import { OverviewContext } from "src/components/providers/overview-provider";
import ReworkTable from "src/scenes/overview/overview-components/details-tables/rework-table";

const ClickBox = styled(Box)`
    background-color: #333333;
    border-radius: 14px;
    margin: 12px;
    padding-left: 16px;
    padding-top: 1px;
    max-height: 400px;
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(95vh - 450px);
`;

const InfoText = styled.p`
    font-weight: 300;
    font-size: 12px;
    margin: 0;
`;

const ChartInfoBox = ({ clickedPoint, type }) => {
    if (!clickedPoint) return null;
    const [clickedPointDetails, setClickedPointDetails] = React.useState(undefined);
    const [loadingDetails, setLoadingDetails] = React.useState(true);
    const { aggregationPeriod, selectedTeam, devsOnly, refresh } = useContext(OverviewContext);
    const getTimeseriesDetails = async () => {
        setLoadingDetails(true);
        let requestUsers;
        let requestTeamId;
        if (devsOnly) {
            if (["all", "integrated"].includes(selectedTeam)) {
                requestUsers = `${selectedTeam}-developers`;
                requestTeamId = undefined;
            } else {
                requestUsers = "team-developers";
                requestTeamId = selectedTeam;
            }
        } else if (["all", "integrated"].includes(selectedTeam)) {
            requestUsers = `${selectedTeam}`;
            requestTeamId = undefined;
        } else {
            requestUsers = "team";
            requestTeamId = selectedTeam;
        }
        try {
            if (
                !clickedPointDetails ||
                clickedPoint.activePayload[0].payload.datetime !== clickedPointDetails?.datetime
            ) {
                const response = await request(`api/data/executive-summary-timeseries-details`, {
                    method: "POST",
                    body: JSON.stringify({
                        settings: {
                            users: requestUsers,
                            teamId: requestTeamId,
                            aggregationPeriod,
                            refresh,
                        },
                        name: type,
                        datetime: clickedPoint.activePayload[0].payload.datetime,
                    }),
                });
                setClickedPointDetails(response);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoadingDetails(false);
        }
    };

    React.useEffect(() => {
        const fetchTimeseriesDetails = async () => {
            await getTimeseriesDetails();
        };
        fetchTimeseriesDetails();
    }, [clickedPoint]);

    if (type === "backlog")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} style={{ width: "230px" }}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "details"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {value === null ? " - " : value}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                </Grid>
            </ClickBox>
        );
    if (type === "bugsDetected")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} style={{ width: "230px" }}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "details"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {value === null ? " - " : value}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                </Grid>
            </ClickBox>
        );
    if (type === "leadTime")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} style={{ width: "230px" }}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "leadTimeGitDetails" &&
                                            key !== "leadTimePmDetails"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {timeSplitter(value, "variable-accuracy")}{" "}
                                                {timeSplitter(value, "text")}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid item md={9} container alignItems="center" justifyContent="center">
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <>
                            <Grid item md={6}>
                                <LeadTimeTable
                                    data={
                                        clickedPointDetails &&
                                        clickedPointDetails.leadTimeGitDetails
                                    }
                                />
                            </Grid>
                            <Grid item md={3}>
                                <LeadTimePmTable
                                    data={
                                        clickedPointDetails && clickedPointDetails.leadTimePmDetails
                                    }
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "throughput")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} style={{ width: "230px" }}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "throughputGitDetails" &&
                                            key !== "throughputPmDetails"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {timeSplitter(value, "variable-accuracy")}{" "}
                                                {timeSplitter(value, "text")}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid item md={9} container alignItems="center" justifyContent="center">
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <>
                            <Grid item md={6}>
                                <ThroughputTable
                                    data={
                                        clickedPointDetails &&
                                        clickedPointDetails.throughputGitDetails
                                    }
                                />
                            </Grid>
                            <Grid item md={3}>
                                <ThroughputPmTable
                                    data={
                                        clickedPointDetails &&
                                        clickedPointDetails.throughputPmDetails
                                    }
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "reviewTime")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} xs={11.9} md={2.9}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "details"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {timeSplitter(value, "variable-accuracy")}{" "}
                                                {timeSplitter(value, "text")}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid
                            item
                            xs={11.9}
                            md={8.9}
                            container
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={11.9} md={8.9}>
                            <ReviewTimeTable
                                data={clickedPointDetails && clickedPointDetails.details}
                            />
                        </Grid>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "investment")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} xs={11.9} md={2.9}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "codeDetails" &&
                                            key !== "collabDetails" &&
                                            key !== "balanceDetails"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {timeSplitter(value, "variable-accuracy")}{" "}
                                                {timeSplitter(value, "text")}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid
                            item
                            xs={11.9}
                            md={8.9}
                            container
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={11.9} md={8.9}>
                            <ArrayTable data={clickedPointDetails && clickedPointDetails.details} />
                        </Grid>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "costHours")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} style={{ width: "230px" }}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        {clickedPoint.activePayload[0].payload && (
                            <Stack direction="column" spacing={1} mb={2}>
                                {Object.entries(clickedPoint.activePayload[0].payload)
                                    .filter(
                                        ([key]) =>
                                            key !== "date" &&
                                            key !== "datetime" &&
                                            key !== "longHoursDetails" &&
                                            key !== "outOfHoursDetails" &&
                                            key !== "focusDetails"
                                    )
                                    .map(([key, value]) => (
                                        <Box key={key}>
                                            <InfoText>
                                                {executiveNameLibrary[key]}:{" "}
                                                {timeSplitter(value, "variable-accuracy")}{" "}
                                                {timeSplitter(value, "text")}
                                            </InfoText>
                                        </Box>
                                    ))}
                            </Stack>
                        )}
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid item container alignItems="center" justifyContent="center">
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <>
                            <Grid item>
                                <ArrayTable
                                    data={clickedPoint.activePayload[0].payload.codeDetails}
                                    label="Code days"
                                />
                            </Grid>
                            <Grid item>
                                <ArrayTableCredit
                                    data={clickedPointDetails && clickedPointDetails.details}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "scopeCreep")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} xs={11.9} md={2.9}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        <Stack direction="row" spacing={2}>
                            {Object.entries(clickedPoint.activePayload[0].payload)
                                .filter(
                                    ([key]) =>
                                        key !== "date" && key !== "datetime" && key !== "details"
                                )
                                .map(([key, value]) => (
                                    <Box key={key}>
                                        <InfoText>
                                            {key}: {value}
                                        </InfoText>
                                    </Box>
                                ))}
                        </Stack>
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid
                            item
                            xs={11.9}
                            md={8.9}
                            container
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={11.9} md={8.9}>
                            <ScopeCreepTable
                                data={clickedPointDetails && clickedPointDetails.details}
                            />
                        </Grid>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "codeSilos")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} xs={11.9} md={2.9}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        <Stack direction="row" spacing={2} mt={2} mb={2}>
                            {Object.entries(clickedPoint.activePayload[0].payload)
                                .filter(
                                    ([key]) =>
                                        key !== "date" && key !== "datetime" && key !== "details"
                                )
                                .map(([key, value]) => (
                                    <Box key={key}>
                                        <InfoText>
                                            {executiveNameLibrary[key]}: {value}
                                        </InfoText>
                                    </Box>
                                ))}
                        </Stack>
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid
                            item
                            xs={11.9}
                            md={8.9}
                            container
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={11.9} md={8.9}>
                            <CodeSilosTable
                                data={clickedPointDetails && clickedPointDetails.details}
                            />
                        </Grid>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "rework")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} xs={11.9} md={2.9}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        <Stack direction="column" spacing={2} mt={2} mb={2}>
                            {Object.entries(clickedPoint.activePayload[0].payload)
                                .filter(
                                    ([key]) =>
                                        key !== "date" && key !== "datetime" && key !== "details"
                                )
                                .map(([key, value]) => (
                                    <Box key={key}>
                                        <InfoText>
                                            {executiveNameLibrary[key]}: {value}
                                        </InfoText>
                                    </Box>
                                ))}
                        </Stack>
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid
                            item
                            xs={11.9}
                            md={8.9}
                            container
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={11.9} md={8.9}>
                            <ReworkTable
                                data={clickedPointDetails && clickedPointDetails.details}
                            />
                        </Grid>
                    )}
                </Grid>
            </ClickBox>
        );
    if (type === "maintainability")
        return (
            <ClickBox>
                <Grid container direction="row" spacing={2}>
                    <Grid item mr={1.5} xs={11.9} md={2.9}>
                        <p>
                            {isValid(new Date(clickedPoint.activePayload[0].payload.datetime)) &&
                                `${format(
                                    new Date(clickedPoint.activePayload[0].payload.datetime),
                                    "EEEE d MMMM yyyy"
                                )}`}
                        </p>
                        <Stack direction="row" spacing={2} mt={2}>
                            {Object.entries(clickedPoint.activePayload[0].payload)
                                .filter(
                                    ([key]) =>
                                        key !== "date" && key !== "datetime" && key !== "details"
                                )
                                .map(([key, value]) => (
                                    <Box key={key}>
                                        <InfoText>
                                            {executiveNameLibrary[key]}: <strong>{value}</strong>
                                        </InfoText>
                                    </Box>
                                ))}
                        </Stack>
                    </Grid>
                    <Divider orientation="vertical" variant="middle" flexItem />
                    {loadingDetails ? (
                        <Grid
                            item
                            xs={11.9}
                            md={8.9}
                            container
                            alignItems="center"
                            justifyContent="center"
                        >
                            <CircularProgress size={50} />
                        </Grid>
                    ) : (
                        <Grid item xs={11.9} md={8.9}>
                            <MaintainabilityTable
                                data={clickedPointDetails && clickedPointDetails.details}
                            />
                        </Grid>
                    )}
                </Grid>
            </ClickBox>
        );
    return null;
};

export default ChartInfoBox;
